// An agent's own conversation on its page (MoET review, 16 Oct 2026; Q20–Q22): the Librarian (since 22 Oct 2026), Economic Radar,
// Country360, Economic Modelling and News & Signals each talk on their own page, with no ECONIC column beside them. Asking (the agent's box under the page header, its prompts, ✦ Dive
// in, a routine's "Set up", a landing's outputs) starts a thread with THAT agent — the server's surface `econic:<space>:agent:<module>`,
// the same ECONIC assistant, tools and figures with the agent's role — and opens it in place: `?chat=<thread>` on the page (every other
// parameter kept), so Back closes it and the box returns. A conversation already open on the page takes the next question. A page of the
// agent without a box of its own (an indicator, a signal) opens the conversation on the agent's page. Policy (PMO) is supplied by the PMO
// and never answers here: on its pages a question goes to ECONIC full screen with the screen in view.
//
//   useModuleAgent(module)        what the box and the conversation need: the open thread, ask / open / close, starting / refused
//   <ModuleAgentDock module/>     mounted by the shell on an agent's pages: the dock's ask (DiveIn, RoutinePresets…) reaches the agent
//   useModuleHost(module)         a box on screen that shows the conversation in place (else asks open it on the agent's page)
//   useThreadRequests / markRequestSent / takeRequest         the question waiting for a conversation (kept until it is sent)
//   usePrefill(module) / clearPrefill                          the words waiting for a box, with the card they are about
//   rememberConversation / freshModuleConversation            "Continue: …" after Back, and ⌘J (a new conversation: the empty box)
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useSearchParams, type NavigateFunction } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useAuth } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import { useAssistantApi, type PanelRequest, type SurfaceFocus } from "@ncp/assistant-sdk";
import { useDockPanel, useReadPublishedSurface, type DockPanel } from "../../shell/dock";
import { agentNames, moduleByKey } from "../modules";
import { useAgentSpace, useAgentSpaceProblem } from "./hooks";
import { handOffToAgent } from "./handoff";
import { agentThreadKey, chatParam, chatState, closeGoesBack, createRequestStore, isChatModule, moduleHome, withChat, type AgentModule } from "./moduleAgentModel";

export { CHAT_MODULES, isChatModule, isAgentModule, moduleOfPath, type AgentModule, type ChatModule } from "./moduleAgentModel";

/** ⌘J on an agent's page: the agent's conversation closes and its empty box takes the focus (event detail: the module key). */
export const MODULE_AGENT_NEW_EVENT = "econic:module-agent:new";

// ---------------------------------------------------------------- the store (per agent, for the session)

interface Status { starting: string | null; refused: string | null }
const NONE: Status = { starting: null, refused: null };

/**
 * The next question of a conversation shown in place, by thread. It stays here until the conversation has sent it (`markRequestSent`), so a
 * card closed (or opened full screen) before its thread loaded still asks it once, and a card opened again never asks it twice.
 */
const requests = createRequestStore<PanelRequest>(() => emit());
/** Words for an agent's box or its open conversation, left unsent (✦ "Ask about this card", a filled prompt). */
export interface ModulePrefill {
  id: string;
  text: string;
  select: [number, number] | null;
  /** What the words are about (the card "Ask about this card" was opened on): sent as `surface.focus` with them. */
  focus: SurfaceFocus | null;
  directive: string | null;
}
const prefills = new Map<AgentModule, ModulePrefill>();
/** The boxes on screen that show a conversation in place. */
const hosts = new Map<AgentModule, number>();
/** The last conversation opened on each agent's page ("Continue: …" once Back closed it). */
const last = new Map<AgentModule, { id: string; title: string | null }>();
const status = new Map<AgentModule, Status>();
let version = 0;
const listeners = new Set<() => void>();
const emit = () => { version += 1; listeners.forEach((l) => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const useVersion = () => useSyncExternalStore(subscribe, () => version, () => version);

const uid = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const setStatus = (m: AgentModule, s: Status) => { status.set(m, s); emit(); };

/** Takes the question waiting for this thread (once): the full screen adopts one a card had not sent yet. */
export function takeRequest(threadId: string | null | undefined): PanelRequest | null {
  return requests.take(threadId);
}

/**
 * A question for a conversation just created elsewhere (a project's "Start a conversation in …"): the full screen takes it once the
 * thread opens at `/agent/{threadId}` and asks it there (AgentPage's `takeRequest`).
 */
export function queueRequest(threadId: string, text: string, directive: string | null = null): void {
  requests.put(threadId, { id: uid(), text, directive });
}

/** The conversation on screen has sent this question: it is never handed to a conversation again. */
export function markRequestSent(threadId: string, id: string): void {
  requests.sent(threadId, id);
}

/** The question waiting for this thread, without taking it (the conversation on screen sends it, then marks it sent). */
export function peekRequest(threadId: string | null | undefined): PanelRequest | null {
  return requests.peek(threadId);
}

/** The questions that arrive for an open conversation while it is on screen. */
export function useThreadRequests(threadId: string | null): PanelRequest | null {
  useVersion();
  return peekRequest(threadId);
}

/** The words waiting for an agent's box (or its open conversation's composer); cleared once placed. */
export function usePrefill(module: AgentModule | null): ModulePrefill | null {
  useVersion();
  return module ? prefills.get(module) ?? null : null;
}

export function clearPrefill(module: AgentModule, id: string): void {
  if (prefills.get(module)?.id === id) { prefills.delete(module); emit(); }
}

/** A conversation the page showed (its title once it has one): offered again as "Continue: …" after it is closed. */
export function rememberConversation(module: AgentModule, id: string, title: string | null): void {
  const was = last.get(module);
  if (was?.id === id && was.title === title) return;
  last.set(module, { id, title });
  emit();
}

/** While mounted, the agent's conversation opens in place on this page. */
export function useModuleHost(module: AgentModule | null, active = true): void {
  useLayoutEffect(() => {
    if (!module || !active) return;
    hosts.set(module, (hosts.get(module) ?? 0) + 1);
    return () => {
      const n = (hosts.get(module) ?? 1) - 1;
      if (n > 0) hosts.set(module, n);
      else hosts.delete(module);
    };
  }, [module, active]);
}

/** ⌘J on an agent's page: the empty box on this page, else the agent's page. */
export function freshModuleConversation(module: AgentModule, navigate: NavigateFunction): void {
  if (isChatModule(module) && (hosts.get(module) ?? 0) > 0) window.dispatchEvent(new CustomEvent(MODULE_AGENT_NEW_EVENT, { detail: module }));
  else navigate(moduleHome(module));
}

// ---------------------------------------------------------------- the hook

export interface ModuleAskOptions {
  /** false leaves the words in the box (or the open conversation's composer) for the person to finish. */
  send?: boolean;
  /** What ✦ Dive in points at (a widget, a row, a figure), sent as `surface.focus`. */
  focus?: SurfaceFocus | null;
  directive?: string | null;
  /** With `send: false`: the characters to select (a prompt's default value to type over). */
  select?: [number, number] | null;
}

export interface ModuleAgentApi {
  module: AgentModule | null;
  /** The agent answers on its own page (Policy (PMO) and the Knowledge Bank do not). */
  chattable: boolean;
  /** "Economic Radar", "Economist". */
  name: string;
  role: string;
  /** The conversation open on the page (`?chat=`). */
  threadId: string | null;
  /** The question of a conversation being started. */
  starting: string | null;
  /** The server refused to start one (a view-only account on a stack that keeps its chat closed). */
  refused: string | null;
  last: { id: string; title: string | null } | null;
  /** Asks the agent; resolves true once the question is on its way (or in the box). */
  ask: (text: string, options?: ModuleAskOptions) => Promise<boolean>;
  open: (threadId: string) => void;
  /** Closes the conversation on the page (Back, when the page opened it). */
  close: () => void;
}


/**
 * `theme`: on a theme's page, a new conversation is the Librarian's about that theme (`kb:theme:<topic>`: her document reads stay within it
 * unless the person asks beyond it).
 */
export function useModuleAgent(module: AgentModule | null, theme?: string | null): ModuleAgentApi {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  const space = useAgentSpace();
  const spaceProblem = useAgentSpaceProblem(space);
  const viewOnly = !!useAuth().user?.viewOnly;
  const readSurface = useReadPublishedSurface();
  useVersion();
  const chattable = isChatModule(module);
  const def = module ? moduleByKey(module) : null;
  // The persona signs the conversation, the product beside it ("The Country Analyst · Country360").
  const names = def && module ? agentNames(module, locale === "ar") : null;
  const name = names?.name ?? "";
  const role = module ? names?.sub ?? t(`assistant:econic.modkit.landing.role.${module}`) : "";
  const threadId = chattable ? chatParam(params.get("chat")) : null;
  const st = (module && status.get(module)) || NONE;

  // Read when asked, never from a render that may be stale (a question arriving from the dock).
  const live = useRef({ space, spaceProblem, locale, viewOnly, location });
  live.current = { space, spaceProblem, locale, viewOnly, location };

  const open = useCallback((id: string) => {
    const q = withChat(live.current.location.search, id).toString();
    navigate({ search: `?${q}` }, { state: chatState(live.current.location.pathname) });
  }, [navigate]);

  const close = useCallback(() => {
    const l = live.current.location;
    if (closeGoesBack(l.state, l.pathname)) { navigate(-1); return; }
    const q = withChat(l.search, null).toString();
    navigate({ search: q ? `?${q}` : "" }, { replace: true });
  }, [navigate]);

  const ask = useCallback(async (text: string, o: ModuleAskOptions = {}): Promise<boolean> => {
    const body = text.trim();
    if (!module || !body) return false;
    // Policy (PMO), the Knowledge Bank: ECONIC full screen, with the screen (and what was pointed at) in view.
    if (!isChatModule(module)) {
      const s = readSurface();
      handOffToAgent(navigate, { text: o.send === false ? null : body, surface: s ? { ...s, ...(o.focus ? { focus: o.focus } : {}) } : null, directive: o.directive ?? null });
      return true;
    }
    const hosted = (hosts.get(module) ?? 0) > 0;
    if (o.send === false) {
      prefills.set(module, { id: uid(), text: body, select: o.select ?? null, focus: o.focus ?? null, directive: o.directive ?? null });
      emit();
      if (!hosted) navigate(moduleHome(module));
      return true;
    }
    const request: PanelRequest = { id: uid(), text: body, focus: o.focus ?? null, directive: o.directive ?? null };
    // A conversation open on this page takes the question.
    const openThread = hosted ? chatParam(new URLSearchParams(live.current.location.search).get("chat")) : null;
    if (openThread) {
      requests.put(openThread, request);
      return true;
    }
    const l = live.current;
    if (status.get(module)?.starting) return false;
    if (!l.space) {
      setStatus(module, { starting: null, refused: l.spaceProblem });
      return false;
    }
    setStatus(module, { starting: body, refused: null });
    try {
      const r = await sdk.agentThread(l.space, { locale: l.locale, agent: agentThreadKey(module, theme) });
      requests.put(r.threadId, request);
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      setStatus(module, NONE);
      if ((hosts.get(module) ?? 0) > 0) open(r.threadId);
      // From a page without a box (an indicator, a signal): the agent's page, where Close and ⌘J close it in place (no state: Back
      // would return to the page the question came from).
      else navigate(`${moduleHome(module)}?chat=${encodeURIComponent(r.threadId)}`);
      return true;
    } catch (e) {
      const refused = e instanceof ApiError && e.status === 403 ? (l.viewOnly ? t("assistant:econic.agent.viewOnlyRefused") : e.message) : null;
      setStatus(module, { starting: null, refused });
      if (!refused) toast.error(t("assistant:econic.agent.startFailed"), { description: e instanceof Error ? e.message : undefined });
      return false;
    }
  }, [module, theme, readSurface, navigate, sdk, qc, t, open]);

  return useMemo(() => ({
    module, chattable, name, role, threadId, starting: st.starting, refused: st.refused, last: (module && last.get(module)) || null, ask, open, close,
  }), [module, chattable, name, role, threadId, st.starting, st.refused, ask, open, close]);
}

// ---------------------------------------------------------------- the dock on an agent's pages

/**
 * The dock's question handler on an agent's pages (the shell mounts it; the ECONIC column is not there): ✦ Dive in, a routine's "Set up",
 * a card's "Ask about this" and the widgets outside a conversation reach the page's own agent. Memoised: it registers on its own renders
 * only, never on the shell's.
 */
export const ModuleAgentDock = memo(function ModuleAgentDock({ module }: { module: AgentModule }) {
  const agent = useModuleAgent(module);
  const ask = useRef(agent.ask);
  ask.current = agent.ask;
  const panel = useMemo<DockPanel>(() => ({
    kind: "surface",
    title: agent.name,
    body: null,
    ask: (text, o) => { void ask.current(text, { send: o.send, focus: o.focus ?? null, directive: o.directive ?? null }); },
  }), [agent.name]);
  useDockPanel(panel);
  return null;
});
